import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, X, CalendarDays, Loader2, Download, Plus, Clock, MapPin, User, Users, Radio, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { NewBookingModal } from "@/components/manager/ManagerCalendar";
import RescheduleModal from "@/components/manager/RescheduleModal";

import RichContent from "@/components/RichContent";
const TYPE_STYLE = {
  appointment: { label: "Appointment", bg: "bg-[#144F75]/10", border: "border-[#144F75]", text: "text-[#144F75]", dot: "#144F75" },
  class: { label: "Class", bg: "bg-[#5C8A72]/10", border: "border-[#5C8A72]", text: "text-[#3E6B54]", dot: "#5C8A72" },
  event: { label: "Event", bg: "bg-[#D98E73]/15", border: "border-[#D98E73]", text: "text-[#A65D3F]", dot: "#D98E73" },
  broadcast: { label: "Broadcast", bg: "bg-[#9E5C7F]/10", border: "border-[#9E5C7F]", text: "text-[#9E5C7F]", dot: "#9E5C7F" },
  blackout: { label: "Blocked", bg: "bg-red-50", border: "border-red-400", text: "text-red-600", dot: "#EF4444" },
};
const STATUS_STYLE = { pending: "opacity-80 border-dashed", cancelled: "opacity-40 line-through", waitlisted: "border-dashed" };
const HOLD_KINDS = ["Staff meeting", "Personal hold", "Travel day", "Prep time"];

const iso = (d) => { const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const fmt12 = (t) => { if (!t) return ""; const [h, m] = t.split(":").map(Number); const ap = h >= 12 ? "pm" : "am"; return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")}${ap}`; };
const dayLabel = (s) => new Date(s + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

export default function UnifiedCalendar({ scope = "public" }) {
  const qc = useQueryClient();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const [view, setView] = useState(isMobile ? "day" : "week");
  const [anchor, setAnchor] = useState(() => new Date());
  const [selected, setSelected] = useState(null);
  const [holdOpen, setHoldOpen] = useState(false);
  const [bookDate, setBookDate] = useState(null);
  const [dragItem, setDragItem] = useState(null);
  const dragRef = useRef(null);
  const [dragOver, setDragOver] = useState("");
  const [dropResched, setDropResched] = useState(null); // { booking, date } when the dropped time isn't open
  const canQuickBook = scope === "center";

  const range = useMemo(() => {
    if (view === "day") return { from: iso(anchor), to: iso(anchor) };
    if (view === "week") { const start = addDays(anchor, -((anchor.getDay() + 7) % 7)); return { from: iso(start), to: iso(addDays(start, 6)) }; }
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const start = addDays(first, -first.getDay());
    return { from: iso(start), to: iso(addDays(start, 41)) };
  }, [anchor, view]);

  const { data, isLoading } = useQuery({
    queryKey: ["cal-feed", scope, range.from, range.to],
    queryFn: () => api.get("/calendar/feed", { params: { scope, dfrom: range.from, dto: range.to } }).then((r) => r.data),
  });
  const items = data?.items || [];
  const byDay = useMemo(() => {
    const m = {};
    for (const it of items) (m[it.date] = m[it.date] || []).push(it);
    for (const k in m) m[k].sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
    return m;
  }, [items]);

  const upcoming = useMemo(() => {
    const today = iso(new Date());
    const cut = iso(addDays(new Date(), 7));
    return items.filter((i) => i.date >= today && i.date <= cut && i.status !== "cancelled").slice(0, 12);
  }, [items]);

  const nav = (dir) => setAnchor((a) => addDays(a, dir * (view === "day" ? 1 : view === "week" ? 7 : 30)));
  const title = view === "month"
    ? anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" })
    : view === "day" ? anchor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })
    : `${dayLabel(range.from)} – ${dayLabel(range.to)}`;

  const exportIcal = async () => {
    try {
      const r = await api.post("/calendar/ical-token");
      const url = `${window.location.origin}${r.data.path}`;
      await navigator.clipboard.writeText(url);
      toast.success("iCal feed URL copied — paste it into Google/Apple/Outlook calendar subscriptions");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not create feed"); }
  };

  const days = useMemo(() => {
    const out = [];
    const start = new Date(range.from + "T00:00:00");
    const end = new Date(range.to + "T00:00:00");
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) out.push(iso(d));
    return out;
  }, [range]);

  const Chip = ({ it }) => {
    const s = TYPE_STYLE[it.type] || TYPE_STYLE.event;
    const draggable = scope === "center" && it.type === "appointment" && it.status !== "cancelled";
    return (
      <button onClick={() => setSelected(it)} data-testid={`cal-item-${it.id}`}
        draggable={draggable}
        onDragStart={draggable ? (e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", it.id); dragRef.current = it; setDragItem(it); } : undefined}
        onDragEnd={draggable ? () => { dragRef.current = null; setDragItem(null); setDragOver(""); } : undefined}
        title={draggable ? "Drag to another day to reschedule" : undefined}
        className={`w-full text-left rounded-md border-l-[3px] ${s.border} ${s.bg} px-1.5 py-1 mb-1 hover:brightness-95 transition ${STATUS_STYLE[it.status] || ""} ${draggable ? "cursor-grab active:cursor-grabbing" : ""} ${dragItem?.id === it.id ? "opacity-40" : ""}`}>
        <p className={`text-[10px] font-semibold ${s.text} truncate`}>{it.start_time && `${fmt12(it.start_time)} `}{it.title}</p>
        {it.type === "appointment" && it.client_name && <p className="text-[9px] text-[#4A5D6B] truncate">{it.client_name}{it.enhancer_name ? ` · ${it.enhancer_name}` : ""}</p>}
        {it.spots_left !== null && it.spots_left !== undefined && it.capacity > 0 && <p className="text-[9px] text-[#8FA89B]">{it.spots_left === 0 ? "Full — waitlist" : `${it.spots_left} spots left`}</p>}
      </button>
    );
  };

  const dropProps = (d) => (scope !== "center" ? {} : {
    onDragOver: (e) => { if (dragRef.current) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dragOver !== d) setDragOver(d); } },
    onDragLeave: () => { if (dragOver === d) setDragOver(""); },
    onDrop: (e) => { e.preventDefault(); dropOn(d); },
  });

  const dropOn = async (d) => {
    const it = dragRef.current;
    dragRef.current = null;
    setDragItem(null); setDragOver("");
    if (!it || !d || d === it.date) return;
    try {
      await api.put(`/manager/appointments/${it.id}/reschedule`, { date: d, start_time: it.start_time });
      toast.success(`Moved to ${dayLabel(d)} at ${fmt12(it.start_time)} — the client has been notified.`);
      qc.invalidateQueries({ queryKey: ["cal-feed"] });
    } catch (e) {
      // Same time not open on that day — let the manager pick from open slots instead.
      toast.info(formatApiError(e.response?.data?.detail) || "That time isn't open — pick a slot.");
      setDropResched({ booking: it, date: d });
    }
  };

  return (
    <div data-testid={`unified-calendar-${scope}`}>
      {/* Toolbar — two rows on mobile, single row on md+ */}
      <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex items-center gap-2">
          <button onClick={() => nav(-1)} data-testid="cal-prev" className="p-2.5 rounded-lg border border-[#E5E0D8] hover:border-[#144F75]/40 min-h-[40px] min-w-[40px] grid place-items-center"><ChevronLeft size={16} /></button>
          <button onClick={() => setAnchor(new Date())} data-testid="cal-today" className="px-3 py-2 rounded-lg border border-[#E5E0D8] text-sm hover:border-[#144F75]/40 min-h-[40px]">Today</button>
          <button onClick={() => nav(1)} data-testid="cal-next" className="p-2.5 rounded-lg border border-[#E5E0D8] hover:border-[#144F75]/40 min-h-[40px] min-w-[40px] grid place-items-center"><ChevronRight size={16} /></button>
          <h3 className="font-serif text-base sm:text-lg text-[#1A242B] ml-1 truncate min-w-0 flex-1">{title}</h3>
        </div>
        <div className="flex items-center gap-1.5 sm:ml-auto flex-wrap">
          <div className="flex items-center gap-1 rounded-full border border-[#E5E0D8] p-1">
            {["day", "week", "month"].map((v) => (
              <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}
                className={`px-3 py-1.5 rounded-full text-xs capitalize min-h-[32px] ${view === v ? "bg-[#144F75] text-white" : "text-[#4A5D6B]"}`}>{v}</button>
            ))}
          </div>
          {(scope === "center" || scope === "enhancer") && (
            <button onClick={() => setHoldOpen(true)} data-testid="cal-add-hold" className="eyw-btn-outline !py-2 text-xs"><Plus size={13} /> Add hold</button>
          )}
          {scope !== "public" && (
            <button onClick={exportIcal} data-testid="cal-export-ical" className="eyw-btn-outline !py-2 text-xs"><Download size={13} /> iCal</button>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-4 text-[11px] text-[#4A5D6B]" data-testid="cal-legend">
        {Object.entries(TYPE_STYLE).map(([k, s]) => (
          <span key={k} className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: s.dot }} /> {s.label}</span>
        ))}
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full border-2 border-dashed border-[#8FA89B]" /> Pending</span>
        {scope === "center" && <span className="text-[#8FA89B] italic hidden md:inline" data-testid="cal-drag-hint">Tip: drag an appointment onto another day to reschedule it.</span>}
      </div>

      <div className={`grid gap-5 ${scope === "center" || scope === "enhancer" ? "lg:grid-cols-[1fr_270px]" : ""}`}>
        <div>
          {isLoading ? <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#144F75]" /></div> : (
            <>
              {view === "month" && (
                /* Wrap in a scroll container so month grid doesn't crush on mobile */
                <div className="overflow-x-auto scroll-x-hidden -mx-1 pb-1">
                  <div className="min-w-[520px]">
                    <div className="grid grid-cols-7 gap-px bg-[#E5E0D8] rounded-xl overflow-hidden border border-[#E5E0D8]" data-testid="cal-month-grid">
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="bg-[#F1EDE6] text-center text-[10px] font-semibold uppercase text-[#8FA89B] py-1.5">{d}</div>)}
                      {days.map((d) => {
                        const inMonth = new Date(d + "T00:00:00").getMonth() === anchor.getMonth();
                        return (
                          <div key={d} className={`min-h-[92px] p-1 group/cell relative ${inMonth ? "bg-white" : "bg-[#FBFAF7]"} ${dragOver === d ? "ring-2 ring-inset ring-[#144F75] bg-[#144F75]/[0.05]" : ""}`} data-testid={`cal-day-${d}`} {...dropProps(d)}>
                            <p className={`text-[10px] mb-0.5 ${d === iso(new Date()) ? "font-bold text-[#D98E73]" : "text-[#8FA89B]"}`}>{Number(d.slice(8))}</p>
                            {(byDay[d] || []).slice(0, 3).map((it) => <Chip key={`${it.type}${it.id}${d}`} it={it} />)}
                            {(byDay[d] || []).length > 3 && <button onClick={() => { setAnchor(new Date(d + "T00:00:00")); setView("day"); }} className="text-[9px] text-[#144F75]">+{byDay[d].length - 3} more</button>}
                            {canQuickBook && (
                              <button onClick={() => setBookDate(d)} data-testid={`cal-quick-book-${d}`} title="New booking"
                                className="absolute top-0.5 right-0.5 h-4 w-4 grid place-items-center rounded text-[#8FA89B] opacity-100 md:opacity-0 md:group-hover/cell:opacity-100 hover:bg-[#144F75] hover:text-white transition"><Plus size={11} /></button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
              {view === "week" && (
                <div className="grid grid-cols-1 md:grid-cols-7 gap-2" data-testid="cal-week-grid">
                  {days.map((d) => (
                    <div key={d} className={`rounded-xl border p-2 min-h-[130px] group/cell relative ${d === iso(new Date()) ? "border-[#D98E73] bg-[#D98E73]/[0.04]" : "border-[#E5E0D8] bg-white"} ${dragOver === d ? "ring-2 ring-[#144F75] bg-[#144F75]/[0.05]" : ""}`} data-testid={`cal-day-${d}`} {...dropProps(d)}>
                      <p className="text-[10px] font-semibold text-[#8FA89B] uppercase mb-1.5">{dayLabel(d)}</p>
                      {(byDay[d] || []).map((it) => <Chip key={`${it.type}${it.id}${d}`} it={it} />)}
                      {!(byDay[d] || []).length && <p className="text-[10px] text-[#C9C2B6]">—</p>}
                      {canQuickBook && (
                        <button onClick={() => setBookDate(d)} data-testid={`cal-quick-book-${d}`} title="New booking"
                          className="absolute top-1.5 right-1.5 h-5 w-5 grid place-items-center rounded-md text-[#8FA89B] opacity-100 md:opacity-0 md:group-hover/cell:opacity-100 hover:bg-[#144F75] hover:text-white transition"><Plus size={12} /></button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {view === "day" && (
                <div className="rounded-xl border border-[#E5E0D8] bg-white p-4 space-y-2" data-testid="cal-day-list">
                  {(byDay[iso(anchor)] || []).map((it) => {
                    const s = TYPE_STYLE[it.type] || TYPE_STYLE.event;
                    return (
                      <button key={`${it.type}${it.id}`} onClick={() => setSelected(it)} data-testid={`cal-item-${it.id}`}
                        className={`w-full text-left rounded-xl border-l-4 ${s.border} ${s.bg} px-4 py-3 ${STATUS_STYLE[it.status] || ""}`}>
                        <p className={`text-sm font-semibold ${s.text}`}>{it.title}</p>
                        <p className="text-xs text-[#4A5D6B] mt-0.5">
                          {it.all_day ? "All day" : `${fmt12(it.start_time)}${it.end_time ? ` – ${fmt12(it.end_time)}` : ""}`}
                          {it.client_name ? ` · ${it.client_name}` : ""}{it.enhancer_name ? ` · ${it.enhancer_name}` : ""}{it.facilitator ? ` · ${it.facilitator}` : ""}
                        </p>
                      </button>
                    );
                  })}
                  {!(byDay[iso(anchor)] || []).length && <p className="text-sm text-[#8FA89B] py-8 text-center">Nothing scheduled this day.</p>}
                  {canQuickBook && (
                    <button onClick={() => setBookDate(iso(anchor))} data-testid="cal-quick-book-day"
                      className="w-full rounded-xl border border-dashed border-[#144F75]/30 py-2.5 text-sm text-[#144F75] hover:bg-[#144F75]/[0.04] flex items-center justify-center gap-1.5"><Plus size={14} /> New booking on this day</button>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {(scope === "center" || scope === "enhancer") && (
          <aside className="rounded-xl border border-[#E5E0D8] bg-white p-4 h-fit" data-testid="cal-upcoming">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#8FA89B] mb-3 flex items-center gap-1.5"><CalendarDays size={13} /> Next 7 days</p>
            <div className="space-y-2.5">
              {upcoming.length === 0 && <p className="text-xs text-[#8FA89B]">Nothing coming up.</p>}
              {upcoming.map((it) => {
                const s = TYPE_STYLE[it.type] || TYPE_STYLE.event;
                return (
                  <button key={`u${it.type}${it.id}`} onClick={() => setSelected(it)} className="w-full text-left flex gap-2 items-start">
                    <span className="h-2 w-2 rounded-full mt-1.5 shrink-0" style={{ background: s.dot }} />
                    <span className="min-w-0">
                      <p className="text-xs font-medium text-[#1A242B] truncate">{it.title}</p>
                      <p className="text-[10px] text-[#8FA89B]">{dayLabel(it.date)} {it.start_time && `· ${fmt12(it.start_time)}`}</p>
                    </span>
                  </button>
                );
              })}
            </div>
          </aside>
        )}
      </div>

      {selected && <DetailPanel item={selected} scope={scope} onClose={() => setSelected(null)} />}
      {dropResched && (
        <RescheduleModal booking={dropResched.booking} initialDate={dropResched.date}
          onClose={() => setDropResched(null)}
          onDone={() => { setDropResched(null); qc.invalidateQueries({ queryKey: ["cal-feed"] }); }} />
      )}
      {holdOpen && <HoldForm scope={scope} onClose={() => setHoldOpen(false)} onSaved={() => { setHoldOpen(false); qc.invalidateQueries({ queryKey: ["cal-feed"] }); }} />}
      {bookDate && <NewBookingModal date={bookDate} onClose={() => setBookDate(null)}
        onBooked={() => { setBookDate(null); qc.invalidateQueries({ queryKey: ["cal-feed"] }); }} />}
    </div>
  );
}

function DetailPanel({ item, scope, onClose }) {
  const qc = useQueryClient();
  const [resched, setResched] = useState(false);
  const s = TYPE_STYLE[item.type] || TYPE_STYLE.event;
  return (
    <div className="fixed inset-0 z-[110] flex justify-end bg-black/30" onClick={onClose} data-testid="cal-detail-panel">
      <div className="bg-white w-full max-w-sm h-full overflow-y-auto shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <span className={`text-[10px] uppercase font-semibold px-2 py-1 rounded-full ${s.bg} ${s.text}`}>{s.label}</span>
          <button onClick={onClose} data-testid="cal-detail-close" className="text-[#8FA89B] hover:text-[#1A242B]"><X size={18} /></button>
        </div>
        <h3 className="font-serif text-2xl text-[#1A242B] leading-tight">{item.title}</h3>
        <div className="mt-4 space-y-2.5 text-sm text-[#4A5D6B]">
          <p className="flex items-center gap-2"><Clock size={14} className="text-[#8FA89B]" /> {dayLabel(item.date)} {item.all_day ? "· All day" : item.start_time && `· ${fmt12(item.start_time)}${item.end_time ? ` – ${fmt12(item.end_time)}` : ""}`}</p>
          {(item.location || item.room) && <p className="flex items-center gap-2"><MapPin size={14} className="text-[#8FA89B]" /> {item.location || item.room}</p>}
          {item.status && <p>Status: <span className={`capitalize font-medium ${item.status === "cancelled" ? "text-red-500" : item.status === "pending" ? "text-[#B5794E]" : "text-[#3E6B54]"}`}>{item.status}</span></p>}
          {item.type === "appointment" && (scope === "center" || scope === "enhancer") && (
            <div className="rounded-xl bg-[#F9F7F3] p-3.5 space-y-1.5" data-testid="cal-appt-details">
              {item.client_name && <p className="flex items-center gap-2"><User size={13} className="text-[#8FA89B]" /> {item.client_name}</p>}
              {item.client_email && <p className="text-xs">{item.client_email}</p>}
              {item.client_phone && <p className="text-xs">{item.client_phone}</p>}
              {item.enhancer_name && <p className="text-xs">Enhancer: {item.enhancer_name}</p>}
              {item.payment_status && <p className="text-xs">Payment: <span className={`font-medium ${item.payment_status === "paid" ? "text-[#3E6B54]" : "text-[#B5794E]"}`}>{item.payment_status}</span></p>}
              {item.notes && <p className="text-xs italic">"{item.notes}"</p>}
              {scope === "center" && item.status !== "cancelled" && (
                <button onClick={() => setResched(true)} data-testid="cal-appt-reschedule" className="eyw-btn !py-2 text-sm inline-flex items-center gap-1.5 mt-1">
                  <Clock size={14} /> Reschedule
                </button>
              )}
            </div>
          )}
          {["event", "class", "broadcast"].includes(item.type) && (
            <>
              {item.facilitator && <p className="flex items-center gap-2"><User size={14} className="text-[#8FA89B]" /> {item.facilitator}</p>}
              {item.capacity > 0 && <p className="flex items-center gap-2"><Users size={14} className="text-[#8FA89B]" /> {item.registered}/{item.capacity} registered{item.spots_left === 0 ? " · Full (waitlist open)" : ` · ${item.spots_left} spots left`}</p>}
              {item.description && <RichContent preview text={item.description} className="text-xs leading-relaxed" />}
              {item.watch_path && <Link to={item.watch_path} data-testid="cal-broadcast-join" className="eyw-btn !py-2 text-sm inline-flex mt-1"><Radio size={14} /> Watch live</Link>}
              {(scope === "public" || scope === "me") && !item.cancelled && (
                <Link to="/events" className="eyw-btn-outline !py-2 text-sm inline-flex mt-1" data-testid="cal-detail-register">View & register</Link>
              )}
            </>
          )}
          {item.type === "blackout" && item.reason && <p className="text-xs italic text-red-500">Reason (internal): {item.reason}</p>}
        </div>
      </div>
      {resched && (
        <RescheduleModal
          booking={{ id: item.id, service_id: item.service_id, enhancer_id: item.enhancer_id,
            service_title: item.title, client_name: item.client_name, date: item.date, start_time: item.start_time }}
          onClose={() => setResched(false)}
          onDone={() => { qc.invalidateQueries({ queryKey: ["cal-feed"] }); onClose(); }} />
      )}
    </div>
  );
}

function HoldForm({ scope, onClose, onSaved }) {
  const [f, setF] = useState({ kind: HOLD_KINDS[0], custom: "", start_date: "", end_date: "", start_time: "", end_time: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.start_date) { toast.error("Pick a date."); return; }
    setBusy(true);
    const body = { start_date: f.start_date, end_date: f.end_date, start_time: f.start_time, end_time: f.end_time, reason: f.custom || f.kind };
    try {
      if (scope === "center") await api.post("/manager/blackouts", body);
      else await api.post("/enhancer/me/blackouts", body);
      toast.success("Hold added — bookings are blocked in that window");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const fld = "eyw-input w-full !py-2 text-sm";
  return (
    <div className="fixed inset-0 z-[110] grid place-items-center bg-black/40 p-4" onClick={onClose} data-testid="cal-hold-form">
      <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif text-xl text-[#1A242B] mb-1">Add a hold</h3>
        <p className="text-xs text-[#8FA89B] mb-4">Staff meetings, personal holds, travel, prep time — blocks new bookings in that window. Leave times empty to block the whole day.</p>
        <div className="space-y-3">
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="hold-kind" className={fld}>
            {HOLD_KINDS.map((k) => <option key={k}>{k}</option>)}
          </select>
          <input placeholder="Custom label (optional)" value={f.custom} onChange={(e) => setF({ ...f, custom: e.target.value })} data-testid="hold-label" className={fld} />
          <div className="flex gap-2">
            <input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="hold-start-date" className={fld} />
            <input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="hold-end-date" className={fld} />
          </div>
          <div className="flex gap-2 items-center">
            <input type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} data-testid="hold-start-time" className={fld} />
            <span className="text-xs text-[#8FA89B]">to</span>
            <input type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} data-testid="hold-end-time" className={fld} />
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button onClick={onClose} className="eyw-btn-outline !py-2 text-sm">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="hold-save" className="eyw-btn !py-2 text-sm disabled:opacity-60">{busy && <Loader2 className="animate-spin" size={13} />} Add hold</button>
          </div>
        </div>
      </div>
    </div>
  );
}
